import * as React from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogClose,
} from '@/components/ui/dialog'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'

type AppDialogProps = {
  /** Contrôlé */
  open?: boolean
  onOpenChange?: (open: boolean) => void
  /** Non contrôlé (optionnel) */
  defaultOpen?: boolean

  /** Titre/description (facultatifs) */
  title?: React.ReactNode
  description?: React.ReactNode

  /** Contenu principal */
  children?: React.ReactNode

  /** Pied de page (boutons, etc.). Si non fourni, aucun footer. */
  footer?: React.ReactNode

  /** Bouton/élément déclencheur (non contrôlé). Ignoré si `open` est fourni */
  trigger?: React.ReactNode

  /** Classes supplémentaires sur le contenu */
  className?: string

  /** Hauteur max (par défaut 90vh) */
  maxHeight?: number | string

  /** Mode formulaire (wrap `children` dans <form>) */
  asForm?: boolean
  onSubmit?: (e: React.FormEvent<HTMLFormElement>) => void
}

export default function AppDialog({
  open,
  onOpenChange,
  defaultOpen,
  title,
  description,
  children,
  footer,
  trigger,
  className,
  maxHeight = '90vh',
  asForm = false,
  onSubmit,
}: AppDialogProps) {
  const Content = (
    <DialogContent
      className={cn('sm:max-w-md', 'overflow-y-auto', className)}
      style={{ maxHeight }}
    >
      {(title || description) && (
        <DialogHeader className='mx-12 mb-3'>
          {title ? <DialogTitle>{title}</DialogTitle> : null}
          {description ? (
            <DialogDescription>{description}</DialogDescription>
          ) : null}
        </DialogHeader>
      )}

      {asForm ? (
        <form onSubmit={onSubmit}>
          {children}
          {footer ? <DialogFooter>{footer}</DialogFooter> : null}
        </form>
      ) : (
        <>
          <div>{children}</div>
          {footer ? <DialogFooter>{footer}</DialogFooter> : null}
        </>
      )}
    </DialogContent>
  )

  // Contrôlé
  if (typeof open === 'boolean') {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        {trigger ? <DialogTrigger asChild>{trigger}</DialogTrigger> : null}
        {Content}
      </Dialog>
    )
  }

  // Non contrôlé avec trigger
  return (
    <Dialog defaultOpen={defaultOpen} onOpenChange={onOpenChange}>
      {trigger ? <DialogTrigger asChild>{trigger}</DialogTrigger> : null}
      {Content}
    </Dialog>
  )
}

/** Footer basique réutilisable pour 80% des cas */
export function ModalActions({
  onConfirm,
  confirmLabel = 'Valider',
  confirmLoading = false,
  confirmDisabled = false,
  confirmVariant = 'default',
  cancelLabel = 'Annuler',
  extra,
}: {
  onConfirm?: () => unknown | Promise<unknown>
  confirmLabel?: string
  confirmLoading?: boolean
  confirmDisabled?: boolean
  confirmVariant?: 'default' | 'destructive' | 'secondary' | 'outline'
  cancelLabel?: string
  extra?: React.ReactNode
}) {
  return (
    <div className='flex w-full items-center justify-between gap-2'>
      <div className='flex items-center gap-2'>{extra}</div>

      <div className='flex items-center gap-2'>
        <DialogClose asChild>
          <Button
            type='submit'
            variant='outline'
            onClick={(e) => e.preventDefault()}
          >
            {cancelLabel}
          </Button>
        </DialogClose>

        <Button
          type='submit'
          variant={confirmVariant}
          onClick={(e) => {
            const cb = onConfirm
            if (cb) {
              e.preventDefault()
              void cb()
            }
          }}
          disabled={confirmLoading || confirmDisabled}
        >
          {confirmLoading ? '...' : confirmLabel}
        </Button>
      </div>
    </div>
  )
}
